<template>
    <div class="w-full mb-3 flex flex-row gap-2">
        <ToggleButton v-model="setBalances" class="w-24 flex text-xs border-round-3xl" onLabel="Балансы" offLabel="Балансы" size="small"/>
        <Button label="Сбросить" @click="clear()" icon="pi pi-refresh" iconPos="right" severity="secondary" class="p-button-sm border-round-md border-0 text-xs font-medium"/>
    </div>
    <DataTable v-if='transactions' :value="transactions" responsiveLayout="scroll" v-model:filters="filters" v-model:selection="selectedRows"
        @page="onPage($event)" @sort="onSort($event)" @filter="onFilter($event)" filterDisplay="menu" 
        :globalFilterFields="['_id', 'from_account_id', 'to_account_id', 'operation_id', 'type', 'hash', 'created_at']" 
        lazy paginator :rows="lazyParams.rows" :rowsPerPageOptions="[5, 10, 20, 50, 100, 200, 300, 400, 500]" :totalRecords="total" :loading="loading" >
        
        <Column selectionMode="multiple" headerStyle="width: 3rem"></Column>
        
        <Column field="id" sortable filterMatchMode="contains" :showFilterMatchModes="false" :exportable="false">
            <template #header>
                <span class="text-xs font-medium">ID</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-sm flex flex-row">
                    <div class="flex flex-row gap-1">
                        <span class="flex my-auto text-xs overflow-hidden" style="max-width:5em">
                            <span class="white-space-nowrap" style="text-overflow:ellipsis;">
                                {{ data.id }}
                            </span>
                        </span>
                        <span>...</span>
                    </div>
                    <Button @click="copy(data.id)" icon="pi pi-copy" iconPos="right" text severity="secondary" class="text-xs" size="small"/>
                </span>
            </template>
        </Column>

        <Column field="amount">
            <template #header>
                <span class="text-xs font-medium">Сумма</span>
            </template>
            <template #body="{ data }">
                <span v-if="data.amount" class="white-space-nowrap flex flex-row gap-1 align-items-center">
                    <span v-if="account_id" 
                        :class="(account_id==data.to_account_id) ? 'text-green-600':'text-orange-600'" 
                        class="white-space-nowrap">
                        {{ (account_id==data.to_account_id) ? '+' : '-' }}{{ getFormatAmount(data.amount, data.currency) }}
                    </span>
                    <span v-else>{{ getFormatAmount(data.amount, data.currency) }}</span>
                    <Button @click="copy(data.amount)" icon="pi pi-copy" iconPos="right" text severity="secondary" class="text-xs" size="small"/>
                </span>
            </template>
            <template #footer>
                <span class="text-xs font-medium">Итого: {{ totalSum }}</span>
            </template>
        </Column>

        <Column field="description">
            <template #header>
                <span class="text-xs font-medium">Описание</span>
            </template>
            <template #body="{ data }">
                <span v-if="data.description" class="flex flex-row gap-2 text-xs white-space-nowrap vertical-align-middle justify-content-start">
                    <span class="flex my-auto" v-tooltip="{ value: data.description }">{{ getShortDescription(data.description) }}</span>
                    <Button @click="copy(data.description)" icon="pi pi-copy" iconPos="right" text severity="secondary" class="text-xs" size="small"/>
                </span>
            </template>
        </Column>

        <Column field="from_account_id" filterField="from_account_id" filterMatchMode="contains" :showFilterMenu="true" :showFilterMatchModes="false">
            <template #header>
                <span class="text-xs font-medium">From AcID</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-sm flex flex-row vertical-align-middle">
                    <div v-if="getSysAccount(data.from_account_id)" class="flex text-xs">
                        <Tag :value="GetWalletNameByLabel(getSysAccount(data.from_account_id))" severity="secondary" size="small" class="text-xs font-normal"/>
                    </div>
                    <div v-else class="flex flex-row gap-1">
                        <span class="flex my-auto text-xs overflow-hidden" style="max-width:5em"
                            :class="(account_id==data.from_account_id) ? 'surface-50 text-600' : ''">
                            <span class="white-space-nowrap" style="text-overflow:ellipsis;">
                                {{ data.from_account_id }}
                            </span>
                        </span>
                        <span>...</span>
                    </div>
                    <Button @click="copy(data.from_account_id)" icon="pi pi-copy" iconPos="right" text severity="secondary" class="text-xs" size="small"/>
                </span>
            </template>
        </Column>

        <Column field="from_balance_before" :showFilterMenu="false" :exportable="false" v-if="selectedColumns.includes('Балансы')">
            <template #header>
                <span class="text-xs font-medium">From Acc Balance</span>
            </template>
            <template #body="{ data }">
                <span class="white-space-nowrap text-sm">
                    <span v-if="data.from_balance_before">{{ getFormatAmount(data.from_balance_before, data.currency, 2) }}</span>
                    <i class="pi pi-arrow-right mx-2" style="font-size:0.75rem;"></i>
                    <span v-if="data.from_balance_after">{{ getFormatAmount(data.from_balance_after, data.currency, 2) }}</span>
                </span>
            </template>
        </Column>

        <Column v-if="account_id" field="direction">
            <template #header>
                <span class="text-xs font-medium">Направление</span>
            </template>
            <template #body="{ data }">
                <span v-if="account_id==data.to_account_id" class="text-xs font-medium bg-green-50 text-green-600 p-1 border-round-md">пополнение</span>
                <span v-if="account_id==data.from_account_id" class="text-xs font-medium bg-orange-100 text-orange-600 p-1 border-round-md">списание</span>
            </template>
        </Column>

        <Column field="to_account_id" filterField="to_account_id" filterMatchMode="contains" :showFilterMenu="true" :showFilterMatchModes="false">
            <template #header>
                <span class="text-xs font-medium">To AcID</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-sm flex flex-row vertical-align-middle">
                     <div v-if="getSysAccount(data.to_account_id)" class="flex text-xs">
                        <Tag :value="GetWalletNameByLabel(getSysAccount(data.to_account_id))" severity="secondary" size="small" class="text-xs font-normal"/>
                    </div>
                    <div v-else class="flex flex-row gap-1">
                        <span class="flex my-auto text-xs overflow-hidden" style="max-width:5em">
                            <span class="white-space-nowrap" style="text-overflow:ellipsis;">
                                {{ data.to_account_id }}
                            </span>
                        </span>
                        <span>... </span>
                    </div>
                    <Button @click="copy(data.to_account_id)" icon="pi pi-copy" iconPos="right" text severity="secondary" class="text-xs" size="small"/>
                </span>
            </template>
        </Column>

        <Column field="to_balance_before" :showFilterMenu="false" :exportable="false" v-if="selectedColumns.includes('Балансы')">
            <template #header>
                <span class="text-xs font-medium">To Acc Balance</span>
            </template>
            <template #body="{ data }">
                <span class="white-space-nowrap text-sm">
                    <span v-if="data.to_balance_before">{{ getFormatAmount(data.to_balance_before, data.currency, 2) }}</span>
                    <i class="pi pi-arrow-right mx-2" style="font-size:0.75rem;"></i>
                    <span v-if="data.to_balance_after">{{ getFormatAmount(data.to_balance_after, data.currency, 2) }}</span>
                </span>
            </template>
        </Column>

        <Column field="status" :showFilterMenu="true" :showFilterMatchModes="false" :showApplyButton="false">
            <template #header>
                <span class="text-xs font-medium">Статус</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <Dropdown v-model="filterModel.value" @change="filterCallback()" :options="statuses" placeholder="Все" class="p-column-filter" :showClear="false">
                    <template #option="slotProps">
                        <Tag v-if="slotProps.option" :value="getStatusTitle(slotProps.option)" :severity="getStatusSeverity(slotProps.option)" class="text-xs"/>
                    </template>
                </Dropdown>
            </template>
            <template #body="{ data }">
                <Tag v-if="data.status" :value="getStatusTitle(data.status)" :severity="getStatusSeverity(data.status)" class="text-xs"/>
            </template>
        </Column>

        <Column field="type" :showFilterMenu="true" :showFilterMatchModes="false" :showApplyButton="false">
            <template #header>
                <span class="text-xs font-medium">Тип</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <Dropdown v-model="filterModel.value" @change="filterCallback()" :options="types" placeholder="Все" class="p-column-filter" :showClear="false">
                    <template #option="slotProps">
                        <div class="inline-block text-sm cursor-pointer align-items-center" >
                            <span class="flex">{{slotProps.option}}</span>
                        </div>
                    </template>
                </Dropdown>
            </template>
            <template #body="{ data }">
                <Tag severity="secondary" :value="data.type" class="text-xs font-normal"/>
            </template>
        </Column>
        
        <Column field="operation_id" filterField="operation_id" filterMatchMode="contains" :showFilterMenu="true" :showFilterMatchModes="false">
            <template #header>
                <span class="text-xs font-medium">OpID</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span v-if="data.operation_id" class="text-sm flex flex-row gap-1">
                    <div class="flex flex-row gap-1">
                        <span class="flex my-auto text-xs overflow-hidden" style="max-width:5em">
                            <span class="white-space-nowrap" style="text-overflow:ellipsis;">
                                {{ data.operation_id }}
                            </span>
                        </span>
                        <span>...</span>
                    </div>
                    <Button @click="copy(data.operation_id)" icon="pi pi-copy" iconPos="right" text severity="secondary" class="text-xs" size="small"/>
                </span>
            </template>
        </Column>

        <!-- <Column field="hash" filterField="hash" filterMatchMode="contains" :showFilterMenu="true" :showFilterMatchModes="false">
            <template #header>
                <span class="text-xs font-medium">Хэш</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <div v-if="data.hash" class="flex flex-row gap-1 text-xs vertical-align-middle justify-content-start">
                    <span class="flex my-auto overflow-hidden" style="max-width:8em">
                        <span class="white-space-nowrap" style="text-overflow:ellipsis;">
                            {{ data.hash }}
                        </span>
                    </span>...
                    <span @click="copy(data.hash)" class="flex p-1 pi pi-copy text-500 text-xs cursor-pointer"></span>
                    <span @click="goToHashBlock(data.hash, 'bep20')" class="flex p-1 pi pi-external-link text-500 text-xs cursor-pointer"></span>
                </div>  
            </template>
        </Column> -->

        <!-- <Column field="scan_transaction">
            <template #header>
                <span class="text-xs font-medium">Scan</span>
            </template>
            <template #body="{ data }">
                <Button label="Scan" @click="detail_info(data.scan_transaction)" :disabled="!data.scan_transaction" 
                    :class="(data.scan_transaction) ? 'bg-primary text-white' : 'surface-400 text-600'"
                    class="p-button-sm border-round-md border-0 text-xs font-medium"/>  
            </template>
        </Column> -->

        <Column field="created_at" sortable filterField="created_at" dataType="date" :showFilterMenu="true" :showFilterMatchModes="false" :showFilterOperator="false">
            <template #header>
                <span class="text-xs font-medium">Создан</span>
            </template>
            <template #filter="{ filterModel }">
                <Calendar v-model="filterModel.value" dateFormat="dd.mm.yy" placeholder="dd.mm.yy" mask="99.99.9999" showTime hourFormat="24"/>
            </template>
            <template #body="slotProps">
                <span class="text-xs flex flex-row">
                    {{ formatDate(slotProps.data.created_at) }}
                </span>
            </template>
        </Column>

        <Column field="updated_at">
            <template #header>
                <span class="text-xs font-medium">Обновлен</span>
            </template>
            <template #body="slotProps">
                <span class="text-xs flex flex-row">
                    {{ formatDate(slotProps.data.updated_at) }}
                </span>
            </template>
        </Column>

        <!-- <Column field="data">
            <template #header>
                <span class="text-xs font-medium">data</span>
            </template>
            <template #body="{ data }">
                <Button label="Data" @click="detail_info(data.data)" :disabled="!data.data" 
                    :class="(data.data) ? 'bg-primary text-white' : 'surface-400 text-600'"
                    class="p-button-sm border-round-md border-0 text-xs font-medium"/>  
            </template>
        </Column> -->

        <template #footer>
            <div class="text-sm flex flex-row gap-2 text-600 font-medium">
                <span class="flex my-auto py-2">Итого: {{ totalRecords }}</span>
                <span v-if="selectedRows && selectedRows.length>0" class="flex my-auto">{{ getSelectedAmount() }}
                    Выбрано: {{ selectedRows.length }} на сумму {{ getFormatAmount(getSelectedAmount(), 'rub') }}
                </span>
                <Button @click="copy(getSelectedAmount())" v-if="selectedRows && selectedRows.length>0"
                    icon="pi pi-copy" iconPos="right" text severity="secondary" class="flex text-xs" size="small"/>
            </div>
        </template>
    </DataTable>
    
    <Dialog header="info" v-model:visible="displayDialog" modal :breakpoints="{'960px': '75vw', '640px': '90vw'}" :style="{width: '50vw'}" >
        <div class="w-full mb-3">
            <span v-for="(d,k) in detail_data" class="block mb-2">
                {{ k }}: <span class="font-medium">{{ d }}</span> <br>
            </span>
        </div>
    </Dialog>
</template>


<script>
import Connect from '@/assets/js/connect.js'
import Helper from '@/assets/js/helper.js'
import { useRoute } from 'vue-router'
import BigNumber from 'bignumber.js'
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';

export default {
    props: ['transactions', 'total', 'account_id', 'loading', 'defaultParams'],
    emits: ['filter', 'lazyParams'],
    data() {
        return {
            displayDialog: false,
            detail_data: null,
            statuses: ['pending', 'completed', 'failed', 'canceled'],
            types: ['transfer', 'partner', 'deposit', 'topup', 'withdraw', 'merchant', 'comission', 'liquidity', 'freeze', 'vcard'],
            lazyParams: this.defaultParams,
            // totalRecords: null,
            filters: {
                _id: {value: '', matchMode: 'contains'},
                from_account_id: {value: '', matchMode: 'contains'},
                to_account_id: {value: '', matchMode: 'contains'},
                status: {value: '', matchMode: 'contains'},
                operation_id: {value: '', matchMode: 'contains'},
                type: {value: '', matchMode: 'contains'},
                hash: {value: '', matchMode: 'contains'},
                created_at: {
                    operator: FilterOperator.AND, 
                    constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] 
                },
            },
            sort: {
                field: '_id',
                order: 1
            },
            systemWallets: [],
            selectedRows: [],
            setBalances: false,
            selectedColumns: []
        }
    },
    watch: {
        setBalances: {
            handler(newVal) {
                if(newVal) this.selectedColumns = ['Балансы']
                else this.selectedColumns = []
            },
            immediate: true
        },
    },
    methods: {
        getFormatAmount(value, currency, digits=2) {
            return this.helper.format_amount(value, currency, digits)
        },
        formatDate(value) {
            return this.helper.format_date(value)
        },
        copy(text){
            this.helper.copy_clipboard(text)
            this.$toast.add({ severity: 'info', summary: 'Скопировано', detail: text, life: 1000 })
        },
        goToAccount(id){
            if(!id) return
            const routeData = this.$router.resolve({ path: '/admin/account/' + id }) 
            window.open(routeData.href, '_blank');
        },
        goToOp(id, new_tab = false){
            if(!id) return
            if(new_tab) window.open(`/admin/operations/?operation_id=${id}`, '_blank')
            else this.$router.push({ path: '/admin/operations/', query: { operation_id: id } })
        },
        getShortId(value){
            if(!value) return ''
            return '..'+this.helper.get_short_id(value)
        },
        getSysAccount(value){
            if(!value) return null
            if(value == this.account_id) return 'current'
            return this.helper.GetSystemAccountName(value, this.account_id, this.systemWallets)
        },
        getClassByLabel(label){
            return this.helper.GetClassByLabel(label)
        },
        GetWalletNameByLabel(label){
            return this.helper.GetWalletNameByLabel(label)
        },
        getShortHash(value){
            if(!value) return ''
            return this.helper.get_short_name(value)
        },
        getShortDescription(value){
            if(!value) return ''
            return this.helper.get_short_name(value, 25)
        },
        goToHashBlock(hash, network='bep20'){
            if(!hash || !network) return
            if(network == 'eth'){
                window.open('https://testnet.etherscan.io/tx/'+hash, '_blank');
            } else if(network == 'bep20'){
                window.open('https://testnet.bscscan.com/tx/'+hash, '_blank');
            }
        },
        detailPage(id, type='account', new_tab = false) {
            if(new_tab){
                const routeData = this.$router.resolve({path: '/admin/'+type+'/' + id});
                window.open(routeData.href, '_blank');
            } else  this.$router.push({ path: '/admin/'+type+'/' + id })
        },
        detail_info(data) {
            this.displayDialog = true
            this.detail_data = data
        },
        onPage(event) {
            this.lazyParams = event
            this.$emit('lazyParams', this.lazyParams)
        },
        onSort(event) {
            if(event.sortField && event.sortOrder){
                this.sort = {
                    field: event.sortField,
                    order: event.sortOrder
                }
            } else {
                this.sort = {
                    field: 'created_at',
                    order: -1
                }
            }
            this.lazyParams.sort = this.sort
            this.$emit('lazyParams', this.lazyParams)
        },
        onFilter() {
            this.lazyParams.filters = this.filters
            this.$emit('filter', this.filters)
        },
        getStatusSeverity(status) {
            return this.helper.getStatusSeverity(status)
        },
        getStatusTitle(status) {
            return this.helper.getStatusTitle(status)
        },
        clear() {
            this.filters = {
                _id: {value: '', matchMode: 'contains'},
                from_account_id: {value: '', matchMode: 'contains'},
                to_account_id: {value: '', matchMode: 'contains'},
                status: {value: '', matchMode: 'contains'},
                operation_id: {value: '', matchMode: 'contains'},
                type: {value: '', matchMode: 'contains'},
            }
            this.lazyParams.filters = this.filters
            this.$emit('filter', this.filters)
        },
        getSelectedAmount() {
            let sum = new BigNumber(0)
            if(this.selectedRows && this.selectedRows.length > 0){
                for (let i = 0; i < this.selectedRows.length; i++) {
                    const amount = (this.selectedRows[i].amount && this.selectedRows[i].amount) ? new BigNumber(this.selectedRows[i].amount) : new BigNumber(0)
                    if(this.account_id){
                        if(this.account_id == this.selectedRows[i].to_account_id) sum = sum.plus(amount)
                        else sum = sum.minus(amount)
                    } else sum = sum.plus(amount)
                }
            }
            return sum.toString()
        },
    },
    computed: {
        totalSum() {
            let sum = new BigNumber(0)
            if(this.transactions && this.transactions.length > 0){
                for (let i = 0; i < this.transactions.length; i++) {
                    const amount = (this.transactions[i].amount && this.transactions[i].amount) ? new BigNumber(this.transactions[i].amount) : new BigNumber(0)
                    if(this.account_id){
                        if(this.account_id == this.transactions[i].to_account_id) sum = sum.plus(amount)
                        else sum = sum.minus(amount)
                    } else sum = sum.plus(amount)
                }
            }
            return this.helper.format_amount(sum, 'usdt')
        },
    },
    async beforeCreate(){
        this.connect = new Connect()
        this.helper = new Helper()
        const route = useRoute()
    },
    async mounted() {
        this.systemWallets = (this.$store.getters.GetAdminWallets) ? this.$store.getters.GetAdminWallets : []
    }
}
</script>